import { useInt } from 'hooks/useInt';

import { AccessRoutes } from 'ui/organisms/AccessRoutes/AccessRoutes';

import { GovernmentClose } from 'landing-pages/government/sections/GovernmentClose/GovernmentClose';
import { GovernmentControlChain } from 'landing-pages/government/sections/GovernmentControlChain/GovernmentControlChain';
import { GovernmentFaq } from 'landing-pages/government/sections/GovernmentFaq/GovernmentFaq';
import { GovernmentHero } from 'landing-pages/government/sections/GovernmentHero/GovernmentHero';
import { GovernmentMandates } from 'landing-pages/government/sections/GovernmentMandates/GovernmentMandates';
import { GovernmentMonitoring } from 'landing-pages/government/sections/GovernmentMonitoring/GovernmentMonitoring';
import { GovernmentOwnershipGraph } from 'landing-pages/government/sections/GovernmentOwnershipGraph/GovernmentOwnershipGraph';
import { GovernmentPipeline } from 'landing-pages/government/sections/GovernmentPipeline/GovernmentPipeline';
import { GovernmentRegis } from 'landing-pages/government/sections/GovernmentRegis/GovernmentRegis';
import { GovernmentRegistryMarquee } from 'landing-pages/government/sections/GovernmentRegistryMarquee/GovernmentRegistryMarquee';
import { GovernmentTestimonial } from 'landing-pages/government/sections/GovernmentTestimonial/GovernmentTestimonial';
import { GovernmentWatch } from 'landing-pages/government/sections/GovernmentWatch/GovernmentWatch';

import styles from './GovernmentPage.module.scss';

type Translate = (key: string) => string;

const getAccessRoutes = (t: Translate) => [
  {
    name: t('Platform'),
    desc: t('Search, screen and export company records from the web application.'),
    actions: [{ label: t('Open the platform'), href: '/platform' }],
  },
  {
    name: 'API',
    desc: t(
      'Query any company by registry identifier. Documented endpoints, JSON responses, provenance on every record.',
    ),
    actions: [{ label: t('Read the API docs'), href: 'https://api.globaldatabase.com/docs/v2/#setup' }],
  },
  {
    name: t('Bulk data feeds'),
    desc: t('Full datasets and scheduled deltas, delivered to your warehouse on your cadence.'),
    actions: [{ label: t('Request a feed'), href: '/book-a-demo' }],
  },
  {
    name: t('Inside your environment'),
    desc: t(
      'Where policy prevents outbound calls, the dataset is delivered for processing entirely within your own estate.',
    ),
    actions: [{ label: t('Scope a deployment'), href: '/contact-us' }],
  },
  {
    name: t('CRM and case systems'),
    desc: (
      <>
        {t('Enrich and verify records inside')} <b>Salesforce</b>, <b>HubSpot</b> {t('and')}{' '}
        <b>Microsoft Dynamics 365</b>
        {t(', against the same registers.')}
      </>
    ),
    actions: [
      { label: 'Salesforce', href: 'https://appexchange.salesforce.com/appxListingDetail?listingId=7d8cb601-add2-47f7-a345-4008cbb0cfe4' },
      { label: 'HubSpot', href: 'https://ecosystem.hubspot.com/marketplace/listing/global-database-intelligence-enrichment' },
      { label: 'Dynamics 365', href: '/microsoft-dynamics-integration' },
    ],
  },
  {
    name: 'MCP',
    desc: t('Connect the dataset to any MCP-compatible agent or tool.'),
    actions: [{ label: t('MCP server'), href: '/mcp-integration' }],
  },
  {
    name: t('Assistant connectors'),
    desc: t('Pull registry data straight into the assistant your teams already use.'),
    actions: [
      { label: t('Add to Claude'), href: 'https://claude.ai/new#directory/connectors/global-database' },
      { label: t('Add to ChatGPT'), href: 'https://chatgpt.com/plugins/plugin_asdk_app_696f807d21a481918a1ed1f43d719ce9' },
    ],
  },
  {
    name: 'Regis AI',
    desc: t('Ask questions in natural language across the dataset and get answers with their sources attached.'),
    actions: [{ label: t('Meet Regis AI'), href: '/regis' }],
  },
];

export const GovernmentPage = () => {
  const { t } = useInt();

  return (
    <div className={styles.wrapper}>
      <GovernmentHero />
      <GovernmentRegistryMarquee />
      <GovernmentControlChain />
      <GovernmentMandates />
      <GovernmentWatch />
      <GovernmentMonitoring />
      <GovernmentRegis />
      <GovernmentPipeline />
      <GovernmentOwnershipGraph />
      <AccessRoutes
        background="navy"
        heading={t('How to access the data.')}
        lede={t('Eight routes into the same primary, government-sourced dataset. Pick the one that fits your stack.')}
        routes={getAccessRoutes(t)}
      />
      <GovernmentTestimonial />
      <GovernmentFaq />
      <GovernmentClose />
    </div>
  );
};
